import { BranchIdentifier } from '@texas/utils/helpers/texas/branchHelpers';
import { httpClient } from '../httpClient/httpClient';
import { BaseSample } from './samplesApi';

export enum ShipmentDirection {
  Outgoing = 1,
  Incoming = 2,
  None = 3,
}

export enum ShipmentState {
  AwaitingDispatch = 1,
  Dispatched = 2,
  Unpacked = 3,
  Lost = 4,
}

export enum ShipmentHistoryEvent {
  Created = 1,
  MarkedAsDispatch = 2,
  MarkedAsLost = 3,
  MarkedAsReceived = 4,
  SamplesAdded = 5,
}

export interface ReceiverUser {
  id: number;
  email: string;
  name: string;
  label: string;
  branchIdentifier?: BranchIdentifier;
}

export interface Shipment {
  id: number;
  note: string;
  trackingNumber: string;
  courier: string;
  dispatchDate: string;
  samplesInShipmentCount: number;
  direction: ShipmentDirection;
  receiverUser: ReceiverUser;
  createdByUserId: number;
  createdByUserName: string;
  createdByUserEmail: string;
  createdByUserBranchName: string | null;
  state: ShipmentState;
  ticketNumbers: number[] | null;
}

export interface ShipmentDetailed {
  id: number;
  note: string;
  trackingNumber: string;
  courier: string;
  dispatchDate: string;
  direction: ShipmentDirection;
  receiverUser: ReceiverUser;
  createdByUserId: number;
  createdByUserName: string;
  createdByUserEmail: string;
  createdByUserBranchName: string | null;
  state: ShipmentState;
  samplesInShipment: SampleShipment[];
  shipmentHistories: ShipmentHistory[];
  ticketNumbers: number[] | null;
}

export interface ShipmentHistory {
  date: string;
  userId: number;
  userName: string;
  userEmail: string;
  event: ShipmentHistoryEvent;
}

export interface CreateShipmentRequest {
  id: number;
  dispatchDate: Date;
  receiverUserId: number | null;
  receiverContactId: number | null;
  courier: string;
  note: string;
  trackingNumber: string;
}

export interface UpdateShipmentRequest {
  dispatchDate: Date;
  note: string;
  trackingNumber: string;
}

export interface SampleShipment extends BaseSample {
  transport: {
    receiverUser: ReceiverUser | null;
  };
}

interface AddTransportRequestToShipmentRequest {
  shipmentId: number;
  transportRequestId: number;
}

interface UnpackRequest {
  sampleIds: number[];
  lostSampleIds: number[];
}

export const shipmentsApi = {
  upcomingFromSupplier: (supplierId: number) =>
    httpClient.get<Shipment[]>(`shipments/upcoming/suppliers/${supplierId}`),
  availableFromTransportRequest: (transportRequestId: number) =>
    httpClient.get<Shipment[]>(
      `shipments/upcoming/transport-requests/${transportRequestId}`,
    ),
  myActiveShipments: () => httpClient.get<Shipment[]>(`shipments/active`),
  createShipment: (request: CreateShipmentRequest) =>
    httpClient.post<Shipment>(`shipments`, { data: request }),
  getCouriers: (branchId: number | null) =>
    httpClient.get<string[]>(`shipments/couriers`, { params: { branchId } }),
  dispatch: () => httpClient.put(`shipments/dispatch`),
  setTrackingNumber: (shipmentId: number, trackingNumber: string) =>
    httpClient.put(`shipments/${shipmentId}/tracking-number`, {
      data: { trackingNumber },
    }),
  updateShipment: (shipmentId: number, request: UpdateShipmentRequest) =>
    httpClient.put<Shipment>(`shipments/${shipmentId}`, { data: request }),
  unpack: (shipmentId: number, request: UnpackRequest) =>
    httpClient.put(`shipments/${shipmentId}/unpack`, { data: request }),
  getShipment: (id: number) =>
    httpClient.get<ShipmentDetailed>(`shipments/${id}`),
  addTransportToShipment: (data: AddTransportRequestToShipmentRequest) =>
    httpClient.post(`shipments/transport-request`, { data }),
  removeTransportFromShipment: (transportRequestId: number) =>
    httpClient.delete(`shipments/transport-request/${transportRequestId}`),
};
